import { useEffect, useMemo, useState } from 'react';

import { WifiIcon } from '../common/Icons';
import { apiClient } from '../../utils/api';

const normalizeBssidKey = (value) => (value || '').replace(/[^0-9A-Fa-f]/g, '').toUpperCase();

function TabularData({ wifiData }) {
    const [manufacturerMap, setManufacturerMap] = useState({});

    const trackedBssids = useMemo(() => {
        if (!wifiData) {
            return [];
        }

        const collect = (bandData) => (bandData?.interference_sources?.bssid ?? []).filter(Boolean);
        return [...collect(wifiData.interference_24), ...collect(wifiData.interference_50)];
    }, [wifiData]);

    useEffect(() => {
        if (!wifiData) {
            setManufacturerMap({});
            return;
        }

        const uniqueKeys = Array.from(
            new Set(trackedBssids.map((bssid) => normalizeBssidKey(bssid)).filter(Boolean))
        );
        const missingKeys = uniqueKeys.filter((key) => !manufacturerMap[key]);

        if (missingKeys.length === 0) {
            return;
        }

        let cancelled = false;

        const fetchManufacturers = async () => {
            const responses = await Promise.all(
                missingKeys.map(async (key) => {
                    try {
                        const data = await apiClient.lookupMac(key);
                        return [key, data?.vendorName ?? ''];
                    } catch (error) {
                        console.error(`Failed to lookup manufacturer for ${key}:`, error);
                        return [key, ''];
                    }
                })
            );

            if (cancelled) {
                return;
            }

            setManufacturerMap((prev) => {
                const next = { ...prev };
                for (const [key, vendorName] of responses) {
                    if (!next[key]) {
                        next[key] = vendorName;
                    }
                }
                return next;
            });
        };

        fetchManufacturers();

        return () => {
            cancelled = true;
        };
        // manufacturerMap intentionally excluded from deps to avoid redundant fetch chains.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackedBssids, wifiData]);

    if (!wifiData || wifiData.records_found === 0) {
        return (
            <div className="no-data-message">
                <p>No tabular data found for selected interval.</p>
            </div>
        );
    }

    const resolveManufacturer = (bssid, fallback = '') => {
        const key = normalizeBssidKey(bssid);
        const mapped = manufacturerMap[key];
        if (mapped && mapped.trim()) {
            return mapped;
        }
        return fallback && fallback.trim() ? fallback : 'Unknown';
    };

    // Build 2.4GHz Interference Sources Table
    const interference_24_rows = [];
    if (wifiData.interference_24?.interference_sources?.bssid) {
        for (let i = 0; i < wifiData.interference_24.interference_sources.bssid.length; i++) {
            interference_24_rows.push(
                <tr key={i}>
                    <td data-label="SSID">{wifiData.interference_24.interference_sources.ssid[i]}</td>
                    <td data-label="BSSID">{wifiData.interference_24.interference_sources.bssid[i]}</td>
                    <td data-label="Freq">{parseInt(wifiData.interference_24.interference_sources.freq[i])}</td>
                    <td data-label="RSSI">{parseInt(wifiData.interference_24.interference_sources.rssi[i])}</td>
                    <td data-label="Manufacturer">
                        {resolveManufacturer(
                            wifiData.interference_24.interference_sources.bssid[i],
                            wifiData.interference_24.interference_sources.manufacturer[i]
                        )}
                    </td>
                </tr>
            );
        }
    }

    // Build 5GHz Interference Sources Table
    const interference_50_rows = [];
    if (wifiData.interference_50?.interference_sources?.bssid) {
        for (let i = 0; i < wifiData.interference_50.interference_sources.bssid.length; i++) {
            interference_50_rows.push(
                <tr key={i}>
                    <td data-label="SSID">{wifiData.interference_50.interference_sources.ssid[i]}</td>
                    <td data-label="BSSID">{wifiData.interference_50.interference_sources.bssid[i]}</td>
                    <td data-label="Freq">{parseInt(wifiData.interference_50.interference_sources.freq[i])}</td>
                    <td data-label="RSSI">{parseInt(wifiData.interference_50.interference_sources.rssi[i])}</td>
                    <td data-label="Manufacturer">
                        {resolveManufacturer(
                            wifiData.interference_50.interference_sources.bssid[i],
                            wifiData.interference_50.interference_sources.manufacturer[i]
                        )}
                    </td>
                </tr>
            );
        }
    }

    return (
        <div className="tabular-data-container">
            <div className="table-section">
                <h3><WifiIcon size="24px" />2.4GHz Interference Sources</h3>
                <table className="interference-table">
                    <thead>
                    <tr>
                        <th>SSID</th>
                        <th>BSSID</th>
                        <th>Frequency</th>
                        <th>RSSI</th>
                        <th>Manufacturer</th>
                    </tr>
                    </thead>
                    <tbody>
                    {interference_24_rows.length > 0 ? interference_24_rows :
                        <tr><td colSpan="5">No interference sources found</td></tr>
                    }
                    </tbody>
                </table>
            </div>

            <div className="table-section">
                <h3><WifiIcon size="24px" />5GHz Interference Sources</h3>
                <table className="interference-table">
                    <thead>
                    <tr>
                        <th>SSID</th>
                        <th>BSSID</th>
                        <th>Frequency</th>
                        <th>RSSI</th>
                        <th>Manufacturer</th>
                    </tr>
                    </thead>
                    <tbody>
                    {interference_50_rows.length > 0 ? interference_50_rows :
                        <tr><td colSpan="5">No interference sources found</td></tr>
                    }
                    </tbody>
                </table>
            </div>

            {/* Debug Info */}
            <div className="debug-section" style={{ marginTop: '20px' }}>
                <details>
                    <summary>Raw Data (Debug)</summary>
                    <pre style={{ background: '#f5f5f5', padding: '10px', overflow: 'auto' }}>
            {JSON.stringify(wifiData, null, 2)}
          </pre>
                </details>
            </div>
        </div>
    );
}

export default TabularData;